/** Choice widgets of the record editor: boolean (a switch with its state in words),
 *  enum (a select over the catalog's choices) and country (a searchable list — ~250
 *  entries are too many to scroll).
 *
 *  An enum whose empty value means something (an App button's behaviour left empty inherits
 *  the operator's) offers it by the record kind's words for it (`config.formatValue` of null,
 *  "Inherit the operator’s"), never as "None". */
import { Autocomplete, Box, MenuItem, Switch, TextField, Typography } from '@mui/material';

import { EMPTY_DISPLAY } from '../../editor/format';
import { useRecordEditor } from '../../editor/RecordEditorProvider';
import type { RecordChoice, RecordWidgetProps } from '../../editor/types';

export function BooleanWidget(p: RecordWidgetProps) {
  const checked = p.value === true;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 40 }}>
      <Switch
        size="small"
        checked={checked}
        disabled={p.disabled}
        onChange={(e) => p.onChange(e.target.checked)}
        inputProps={{ id: p.id, 'aria-describedby': p.describedBy }}
      />
      {/* The state in words — the switch's position alone is not enough. */}
      <Typography variant="body2" aria-hidden>
        {checked ? 'Yes' : 'No'}
      </Typography>
    </Box>
  );
}

export function EnumWidget(p: RecordWidgetProps) {
  const { field } = p;
  const { formatValue } = useRecordEditor();
  const known = field.choices.some((c) => c.value === p.value);
  // The kind's words for an empty value, when it has some ("Inherit the operator’s").
  const worded = field.nullable ? formatValue(field, p.rec, null) : EMPTY_DISPLAY;
  const emptyLabel = worded && worded !== EMPTY_DISPLAY ? worded : null;
  return (
    <TextField
      select
      id={p.id}
      size="small"
      fullWidth
      error={p.error}
      disabled={p.disabled}
      value={known ? p.value : ''}
      onChange={(e) => {
        const raw = e.target.value as unknown;
        const choice = field.choices.find((c) => String(c.value) === String(raw));
        p.onChange(choice ? choice.value : null);
      }}
      SelectProps={{
        labelId: p.labelId,
        inputProps: { 'aria-describedby': p.describedBy },
        displayEmpty: true,
        renderValue: (v) =>
          v === '' ? (
            (emptyLabel ?? (
              <Typography component="span" variant="body2" color="text.disabled">
                —
              </Typography>
            ))
          ) : (
            (field.choices.find((c) => c.value === v)?.label ?? String(v))
          ),
      }}
    >
      {field.nullable && <MenuItem value="">{emptyLabel ?? <em>None</em>}</MenuItem>}
      {field.choices.map((c) => (
        <MenuItem key={String(c.value)} value={c.value}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function CountryWidget(p: RecordWidgetProps) {
  const { field } = p;
  const selected = field.choices.find((c) => c.value === p.value) ?? null;
  return (
    <Autocomplete<RecordChoice, false, false, false>
      id={p.id}
      size="small"
      fullWidth
      disabled={p.disabled}
      options={field.choices}
      value={selected}
      onChange={(_, v) => p.onChange(v ? v.value : null)}
      getOptionLabel={(c) => c.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderOption={(props, c) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <li key={key} {...rest}>
            <Box component="span" sx={{ flex: 1 }}>
              {c.label}
            </Box>
            <Typography component="span" variant="caption" color="text.secondary">
              {String(c.value)}
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={p.error}
          inputProps={{ ...params.inputProps, 'aria-describedby': p.describedBy }}
        />
      )}
    />
  );
}
